// ---------------------------------------------------------------------------
// The standings strip: a compact row of totals that sits above the play area.
//
// It must never compete with the tiles. That is the whole design constraint, so
// it stays one line of small chips, scrolls sideways rather than wrapping into a
// second row, and gives YOUR chip a fill instead of a size.
//
// One chip is one score, not one person. While teams are on, a team is a single
// bucket in `teamScores`, so drawing a chip per player showed the same total
// three times over and read as three sides on 42 points each. Teams get one
// chip, players with no team keep theirs, and the strip adds up.
//
// Colour comes from `displayColorOf` for the same reason: while teams are on the
// team owns the colour outright, and a swatch has to mean one thing.
//
// **A colour can be null and the swatch is then not drawn.** Past
// `COLORED_SEATS` seats nobody in the room has one — `colorForSeat` decides that
// once and decides it for the whole room — so this is every chip on the strip
// rather than one odd one out, and a grey stand-in would be a colour that reads
// as an identity. Above that line the room is told apart by name, which is what
// the chip already says.
//
// **And past `STRIP_MAX` the strip stops being one chip per score.** A room of
// five hundred is five hundred chips in a sideways scroller nobody will ever
// reach the end of, and the strip is read at a glance during a round rather than
// scrolled. What survives is what it is read FOR: who is winning, where you are,
// and how many of you there are. See `entriesOf` and the note on the overflow
// chip.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';
import type { ClientGameState } from '@countdown/shared';
import { displayColorOf, membersOfTeam, scoreFor, seatedPlayers } from '../../lib/playerLookup.js';

/**
 * How many chips the strip draws before it starts counting instead.
 *
 * Eight, because eight `max-w-24` chips is about what fits a phone before the
 * scroller starts hiding things, and because a strip that has to be scrolled to
 * find your own score is not a glance. It is not `COLORED_SEATS`: this is about
 * how much fits on one line, and that is about how many colours there are, and
 * the two numbers agreeing would be a coincidence rather than a reason.
 */
const STRIP_MAX = 8;

interface Entry {
  key: string;
  name: string;
  color: string | null;
  total: number;
  /** Yours, or your team's. The one chip worth finding at a glance. */
  yours: boolean;
}

function entriesOf(state: ClientGameState): Entry[] {
  const players = seatedPlayers(state);

  if (state.teams.length === 0) {
    return players.map((player) => ({
      key: player.id,
      name: player.displayName,
      color: displayColorOf(state, player),
      total: scoreFor(state, player),
      yours: player.id === state.youId,
    }));
  }

  const entries: Entry[] = [];
  for (const team of state.teams) {
    const members = membersOfTeam(state, team.id);
    // An empty team is not a side, it is a setting nobody used.
    if (members.length === 0) continue;
    entries.push({
      key: team.id,
      name: team.name,
      color: team.color,
      total: scoreFor(state, members[0]),
      // A team chip is yours when you are on that side, not when you are the
      // member whose answer happened to score it.
      yours: members.some((member) => member.id === state.youId),
    });
  }
  for (const player of players) {
    if (player.teamId !== null) continue;
    entries.push({
      key: player.id,
      name: player.displayName,
      color: displayColorOf(state, player),
      total: scoreFor(state, player),
      yours: player.id === state.youId,
    });
  }
  return entries;
}

/**
 * The chips a large room actually gets: the leaders, and your own.
 *
 * **Your chip is kept wherever it is and never duplicated**, which is the same
 * rule the reveal's standing follows: a strip showing one player twice leaves a
 * reader working out which of the two they are. So the top scores are taken in
 * order and yours is appended only if it was not among them, which also means it
 * is at the end where it is easy to find rather than buried in a run of chips
 * that all look alike.
 *
 * Returned with the count of what is left over, because dropping four hundred
 * and ninety chips without saying so is the silent cap CLAUDE.md is about.
 */
function stripEntries(entries: Entry[]): { shown: Entry[]; hidden: number } {
  if (entries.length <= STRIP_MAX) return { shown: entries, hidden: 0 };

  const ranked = [...entries].sort((a, b) => b.total - a.total);
  const shown = ranked.slice(0, STRIP_MAX - 1);
  const yours = ranked.find((entry) => entry.yours);
  if (yours !== undefined && !shown.includes(yours)) shown.push(yours);

  return { shown, hidden: entries.length - shown.length };
}

export function StandingsStrip(props: {
  state: ClientGameState;
  /** `Round 2 of 5`, right-aligned. */
  showRoundCounter?: boolean;
}): ReactElement {
  const { state, showRoundCounter = false } = props;
  const { shown, hidden } = stripEntries(entriesOf(state));

  return (
    // The counter sits outside the scroller rather than inside it on `ml-auto`.
    // An auto margin distributes slack, and a row that has overflowed into
    // scrolling has none, so the counter stopped being right-aligned at exactly
    // the table sizes that need it most. Outside it, the counter is always at
    // the right edge and never scrolls away with the chips.
    <div className="flex items-center gap-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 overflow-x-auto">
        {shown.map((entry) => {
          return (
            <span
              key={entry.key}
              data-you={entry.yours ? 'true' : undefined}
              // **Yours, not the leader's.** This filled whichever chip held the
              // top score, which is the one thing on the strip a player can work
              // out for themselves — it is sorted, and the number is printed
              // right there. What they actually hunt for in a row of sixteen is
              // their own. Isaac raised it about the reveal's standing and it
              // was true here too, on the strip he sees every single round.
              //
              // It also never marked one player: on a tie it washed every tied
              // chip at once, so early in a game with everyone on zero the
              // highlight said nothing at all.
              //
              // Colour is the only thing that moves between the two states. A
              // chip that changed size would shove the whole strip sideways
              // every time somebody scored.
              className={`flex shrink-0 items-center gap-1.5 rounded-chip px-2 py-1.5 transition-colors duration-300 ${
                entry.yours ? 'bg-blue-700/40' : 'bg-surface-2'
              }`}
            >
              {/* No colour, no swatch. See the note at the head of the file:
                  this is the whole room or none of it, so a strip with no
                  swatches is a strip of names rather than a strip with holes. */}
              {entry.color === null ? null : (
                <span
                  aria-hidden="true"
                  className="h-1.5 w-1.5 rounded-full"
                  style={{ background: entry.color }}
                />
              )}
              <span className="max-w-24 truncate text-[13px] font-semibold text-ink-muted">
                {entry.name}
              </span>
              <span className="font-mono text-[12px] font-bold tabular">{entry.total}</span>
            </span>
          );
        })}

        {/* What was left off, said rather than dropped. It is not a chip anybody
            can press: the standing is a slide of the reveal and the whole room
            is on it, so this only has to stop the strip claiming to be the
            room. */}
        {hidden === 0 ? null : (
          <span
            data-hidden-players={hidden}
            className="shrink-0 rounded-chip bg-surface-2 px-2 py-1.5 font-mono text-[12px] whitespace-nowrap text-ink-dim tabular"
          >
            +{hidden} more
          </span>
        )}
      </div>

      {showRoundCounter && (
        <span className="shrink-0 font-mono text-[11px] whitespace-nowrap text-ink-dim tabular">
          Round {state.currentRoundIndex + 1} of {state.totalRounds}
        </span>
      )}
    </div>
  );
}
